<script lang="ts">
    // Types
    import type { PlaybackItemLayout } from '~/components/structure/Players/components/MobileNowPlaying/types';
    import type { QueueItemMetadata } from '~/jet/models';

    // Components
    import FallbackArtwork from '~/components/jet/Artwork/fallbacks/classical-symbol-square.svelte';

    // Shred Components
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';

    export let item: QueueItemMetadata | null = null;
    export let layout: PlaybackItemLayout = 'large';

    $: isPlaceholder = !item;

    $: artwork = item?.artwork;
</script>

<div
    data-testid="now-playing-artwork"
    class="now-playing-artwork"
    class:now-playing-artwork--large={layout === 'large'}
    class:now-playing-artwork--landscape={layout === 'landscape'}
    class:now-playing-artwork--compact={layout === 'compact'}
    class:is-placeholder={isPlaceholder}
>
    <Artwork
        {artwork}
        profile="info-fullscreen"
        useContainerStyle={false}
        forceFullWidth={false}
        disableAutoCenter={true}
    >
        <svelte:fragment slot="placeholder-component">
            {#if FallbackArtwork}
                <div class="artwork-fallback" data-testid="artwork-fallback">
                    <svelte:component this={FallbackArtwork} />
                </div>
            {/if}
        </svelte:fragment>
    </Artwork>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/after-shadow' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use '../../../../../styles/music-classical/core/globalvars' as *;

    // Full-size styles
    $artwork-radius: $global-border-radius-large;
    $artwork-shadow-offset: 20px;
    $artwork-shadow-blur: 25px;
    $artwork-shadow-spread: -15px;
    $artwork-shadow-color: rgba(0, 0, 0, 0.22);

    // Compact styles
    $artwork-compact-radius: $global-border-radius-small;
    $artwork-compact-shadow-offset: 15px;
    $artwork-compact-shadow-blur: 15px;
    $artwork-compact-shadow-spread: -12px;
    $artwork-compact-shadow-color: rgba(0, 0, 0, 0.18);

    .now-playing-artwork {
        --artwork-override-width: auto;
        --artwork-override-height: 100%;
        --artwork-override-max-width: 100%;
        --artwork-override-max-height: 100%;
        --shadowOffset: #{$artwork-shadow-offset};
        --shadowBlur: #{$artwork-shadow-blur};
        --shadowSpread: #{$artwork-shadow-spread};
        --shadowColor: #{$artwork-shadow-color};
        height: 100%;

        // Remove overlay border to avoid aspect-ratio mismatches
        :global(.artwork-component.artwork-component--has-borders) {
            &::after {
                display: none;
            }
        }

        &.is-placeholder {
            --genericJoeColor: rgba(17, 17, 17, 0.04);
            --icon: rgba(0, 0, 0, 0.15);
        }

        :global(.artwork-component) {
            display: contents;

            // Prevent container from flashing artwork styles while image is loading
            // prettier-ignore
            &:not(.artwork-component--downloaded, .artwork-component--error) {
                visibility: hidden;
            }
        }

        :global(.artwork-component__image) {
            object-fit: contain;
            border-radius: var(--artworkRadius, #{$artwork-radius});
            box-shadow: 0 var(--shadowOffset) var(--shadowBlur)
                var(--shadowSpread) var(--shadowColor);
            aspect-ratio: 1 auto;
            display: inline-block;

            @include absolute-center;
        }
    }

    .now-playing-artwork--large,
    .now-playing-artwork--compact {
        --artwork-override-height: auto;
        --artwork-override-max-width: calc(100dvw - (32px * 2));
        --artwork-override-max-height: calc(100dvh - 335px);
        display: contents;

        @include absolute-center(transform);

        // Empty/Error state / fallback artwork container
        :global(.artwork-component__contents:has(.artwork-fallback)) {
            display: contents;
        }

        // Empty state / fallback artwork sizing adjustments
        .artwork-fallback {
            width: 100%;
            height: 100%;
            background: transparent;

            @include absolute-center;

            :global(svg) {
                width: inherit;
                height: inherit;
                border-radius: inherit;
            }
        }
    }

    .now-playing-artwork--large {
        max-height: calc(100% - 30px);
    }

    .now-playing-artwork--compact {
        --artwork-override-max-width: 100%;
        --artwork-override-max-height: #{$compact-artwork-size};

        :global(.artwork-component__image) {
            border-radius: var(--artworkRadius, #{$artwork-compact-radius});
        }
    }
</style>
